import { ApiCore } from '@monorepo/api'
import { AFFILIATE_ALAN_QUERY_KEYS, QUERY_KEYS } from './types'
import { isNil } from './isNil'
import { isNilOrEmpty } from './isNilOrEmpty'

const AFFILIATE_STORAGE_TTL_MS = 14 * 24 * 60 * 60 * 1000
const AFFILIATE_STORAGE_KEY_ALAN = 'app.affiliate.alan_'

export type AffiliateAlanQuery = {
  [key in (typeof AFFILIATE_ALAN_QUERY_KEYS)[number]]: string
}

type StoredValue = {
  value: string
  expiresAt?: number
}

const setWithExpiry = (key: string, value: string) => {
  const expiresAt = Date.now() + AFFILIATE_STORAGE_TTL_MS
  const payload: StoredValue = { value, expiresAt }

  localStorage.setItem(key, JSON.stringify(payload))
}

const getWithExpiry = (key: string): string | undefined => {
  const rawValue = localStorage.getItem(key)
  if (!rawValue) return

  try {
    const parsedValue = JSON.parse(rawValue) as StoredValue
    // todo выпилить if ниже через 1-2 месяца
    if (!parsedValue?.value || typeof parsedValue.expiresAt !== 'number') {
      setWithExpiry(key, rawValue)

      return rawValue
    }

    if (Date.now() > parsedValue.expiresAt) {
      localStorage.removeItem(key)

      return
    }

    return parsedValue.value
  } catch {
    setWithExpiry(key, rawValue)

    return rawValue
  }
}

export const AffiliateAlanStorage = {
  prefix: AFFILIATE_STORAGE_KEY_ALAN,
  read(key: string): string | undefined {
    return getWithExpiry(this.prefix + key)
  },
  readNumeric(key: string): number | undefined {
    const rawNum = this.read(key)
    if (isNilOrEmpty(rawNum)) return

    const num = Number(rawNum)
    if (!Number.isInteger(num)) return

    return num
  },
  set(data: Partial<AffiliateAlanQuery>) {
    Object.entries(data).forEach(([key, value]) => {
      if (!isNil(value)) setWithExpiry(this.prefix + key, String(value))
    })
  },
  get(): ApiCore.AlanUserRegistrationSchema | undefined {
    const alan_click_id = this.read(QUERY_KEYS.AFFILIATE_ALAN_CLICK_ID)
    const partner_id = this.readNumeric(QUERY_KEYS.AFFILIATE_ALAN_PARTNER_ID)
    const offer_id = this.readNumeric(QUERY_KEYS.AFFILIATE_ALAN_OFFER_ID)
    const creative_id = this.readNumeric(QUERY_KEYS.AFFILIATE_ALAN_CREATIVE_ID)

    if (
      isNil(alan_click_id) ||
      isNil(partner_id) ||
      partner_id <= 0 ||
      isNil(offer_id) ||
      isNil(creative_id)
    )
      return

    return {
      sub_id_1: this.read(QUERY_KEYS.AFFILIATE_ALAN_SUB_ID_1),
      sub_id_2: this.read(QUERY_KEYS.AFFILIATE_ALAN_SUB_ID_2),
      sub_id_3: this.read(QUERY_KEYS.AFFILIATE_ALAN_SUB_ID_3),
      sub_id_4: this.read(QUERY_KEYS.AFFILIATE_ALAN_SUB_ID_4),
      sub_id_5: this.read(QUERY_KEYS.AFFILIATE_ALAN_SUB_ID_5),
      sub_id_6: this.read(QUERY_KEYS.AFFILIATE_ALAN_SUB_ID_6),
      sub_id_7: this.read(QUERY_KEYS.AFFILIATE_ALAN_SUB_ID_7),
      sub_id_8: this.read(QUERY_KEYS.AFFILIATE_ALAN_SUB_ID_8),
      sub_id_9: this.read(QUERY_KEYS.AFFILIATE_ALAN_SUB_ID_9),
      sub_id_10: this.read(QUERY_KEYS.AFFILIATE_ALAN_SUB_ID_10),
      /* landing_id: this.read(QUERY_KEYS.AFFILIATE_ALAN_LANDING_ID), */
      alan_click_id,
      partner_id,
      offer_id,
      creative_id,
    }
  },
}
